/**
 * La red de fibra: qué hay, dónde está y quién cuelga de dónde.
 *
 * ── Por qué esta pantalla nace ahora ──
 *
 * El motor de red ya existía: 60 direcciones en `/api/red`, 17 pruebas corriendo en cada
 * despliegue, tareas automáticas leyendo el router cada 3 horas. Lo que no existía era la
 * pantalla — en `App.js`, `/red` redirigía al escritorio. Meses de información recogida que
 * nadie podía ver. Víctor lo pidió el 20-sep-2026: «incluye la red, pero solo la de fibra».
 *
 * ── Qué decisión de diseño manda aquí ──
 *
 * **Solo fibra.** El motor también sabe de radioenlaces (Caloto), pero eso no es lo que se
 * pidió y mezclarlo llenaría la pantalla de cosas que estorban. Los datos de radio siguen
 * recogiéndose y sus pruebas siguen pasando; simplemente no se pintan aquí.
 *
 * **Nada se inventa.** Todo lo que se muestra sale de un endpoint que ya existía. Donde no hay
 * datos —splitters, tramos y empalmes están en cero— la pantalla lo DICE y explica qué falta
 * cargar, en vez de enseñar un cero mudo que parece un error del sistema.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import { CAPAS, ETIQUETAS, ponerCapa } from '../components/comunes/capasMapa';
import ElegirUbicacion from '../components/comunes/ElegirUbicacion';

/** El color dice el estado de un vistazo, y es el mismo en toda la pantalla. */
const COLOR = {
  ok: '#22C55E', aviso: '#FCD34D', malo: '#EF4444',
  frio: '#64748B', acento: '#00E5FF', azul: '#3B82F6',
};

const ICONO_SITIO = {
  olt: '🏢', nodo: '🗄️', mufa: '🔗', caja: '📦', poste: '📍',
  torre: '🗼', nap: '📦', splitter: '🔀',
};

const s = {
  cuerpo:  { padding: '18px 22px 40px', color: '#fff' },
  h1:      { fontSize: '21px', fontWeight: 700, margin: 0 },
  sub:     { fontSize: '13px', color: 'rgba(255,255,255,0.5)', marginTop: '4px' },
  pestanas:{ display: 'flex', gap: '8px', margin: '18px 0 16px', flexWrap: 'wrap' },
  pest:    { padding: '8px 16px', borderRadius: '9px', border: '1px solid rgba(255,255,255,0.14)',
             background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.75)',
             fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  pestOn:  { background: 'rgba(0,229,255,0.14)', color: COLOR.acento, borderColor: 'rgba(0,229,255,0.5)' },
  tarjetas:{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: '12px' },
  tarjeta: { background: 'rgba(255,255,255,0.045)', border: '1px solid rgba(255,255,255,0.09)',
             borderRadius: '12px', padding: '14px 16px', minWidth: 0 },
  rotulo:  { fontSize: '11px', textTransform: 'uppercase', letterSpacing: '.7px',
             color: 'rgba(255,255,255,0.45)', fontWeight: 700 },
  cifra:   { fontSize: '26px', fontWeight: 800, marginTop: '5px', lineHeight: 1.1 },
  pie:     { fontSize: '11.5px', color: 'rgba(255,255,255,0.45)', marginTop: '5px' },
  panel:   { background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.09)',
             borderRadius: '12px', padding: '16px', marginTop: '14px' },
  tituloP: { fontSize: '14px', fontWeight: 700, marginBottom: '10px' },
  tabla:   { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th:      { textAlign: 'left', padding: '8px 6px', color: 'rgba(255,255,255,0.45)',
             fontSize: '11px', textTransform: 'uppercase', letterSpacing: '.6px',
             borderBottom: '1px solid rgba(255,255,255,0.1)', fontWeight: 700 },
  td:      { padding: '9px 6px', borderBottom: '1px solid rgba(255,255,255,0.055)' },
  aviso:   { background: 'rgba(252,211,77,0.09)', border: '1px solid rgba(252,211,77,0.3)',
             borderRadius: '10px', padding: '12px 14px', fontSize: '13px',
             color: '#FDE68A', lineHeight: 1.6, marginTop: '14px' },
  mapa:    { height: '520px', borderRadius: '12px', overflow: 'hidden',
             border: '1px solid rgba(255,255,255,0.1)' },
  input:   { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
             borderRadius: '8px', padding: '9px 12px', color: 'rgba(255,255,255,0.9)',
             fontSize: '13px', outline: 'none', fontFamily: 'inherit' },
  pill:    { display: 'inline-block', padding: '2px 8px', borderRadius: '20px',
             fontSize: '11px', fontWeight: 700 },
};

const pill = (texto, color) => (
  <span style={{ ...s.pill, background: color + '22', color, border: '1px solid ' + color + '55' }}>{texto}</span>
);

/** «hace 3 horas», que es como uno piensa en «¿esto está al día?». */
const haceCuanto = (f) => {
  if (!f) return 'nunca';
  const min = Math.floor((Date.now() - new Date(f).getTime()) / 60000);
  if (min < 2) return 'hace un momento';
  if (min < 60) return `hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `hace ${h} h`;
  return `hace ${Math.floor(h / 24)} días`;
};

export default function Red() {
  const [vista, setVista] = useState('resumen');
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [resumen, setResumen] = useState(null);
  const [mapa, setMapa] = useState(null);
  const [arbol, setArbol] = useState(null);
  const [sitios, setSitios] = useState([]);
  const [abierto, setAbierto] = useState({});

  const cargar = useCallback(async () => {
    setCargando(true); setError('');
    try {
      /**
       * `solo=fibra` no es un adorno: `red_sitios` guarda las dos redes en la misma tabla y de
       * los 13 sitios, 12 son torres de radio. Sin este filtro, una pantalla titulada «Red de
       * fibra» enseñaba la red de radio — y quien la mira decide sobre lo que cree que ve.
       * La regla de qué es fibra vive en el servidor (`services/tiposRed.js`), no aquí.
       */
      const [r, m, a, si] = await Promise.all([
        api.get('/api/red/resumen?solo=fibra'),
        api.get('/api/red/mapa?solo=fibra'),
        api.get('/api/red/arbol?solo=fibra'),
        api.get('/api/red/sitios?solo=fibra'),
      ]);
      setResumen(r.data); setMapa(m.data); setArbol(a.data); setSitios(si.data || []);
    } catch (e) {
      setError(e.response?.data?.error || 'No se pudo leer la red');
    } finally { setCargando(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  /** Cuántos sitios y cuántos de ellos tienen coordenadas. */
  const totalSitios = (resumen?.sitios || []).reduce((n, x) => n + x.total, 0);
  const ubicados    = (resumen?.sitios || []).reduce((n, x) => n + x.ubicados, 0);
  const fibra       = (resumen?.clientes || []).find(c => /fibra/i.test(c.tecnologia));

  return (
    <Layout>
      <div style={s.cuerpo}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '220px' }}>
            <h1 style={s.h1}>🕸️ Red de fibra</h1>
            <div style={s.sub}>
              Qué hay tendido, dónde está y quién cuelga de dónde.
              {resumen?.ultima_lectura?.corrio_en &&
                <> · Última lectura del router {haceCuanto(resumen.ultima_lectura.corrio_en)}</>}
            </div>
          </div>
          <button type="button" onClick={cargar} disabled={cargando}
            style={{ ...s.pest, ...(cargando ? {} : s.pestOn) }}>
            {cargando ? 'Leyendo…' : '↻ Actualizar'}
          </button>
        </div>

        {error && <div style={{ ...s.aviso, color: '#FCA5A5', background: 'rgba(239,68,68,0.1)',
                                borderColor: 'rgba(239,68,68,0.35)' }}>{error}</div>}

        <div style={s.pestanas}>
          {[['resumen', '📊 Resumen'], ['mapa', '🗺️ Mapa'], ['arbol', '🌳 Árbol'],
            ['sitios', '📍 Sitios'], ['cargar', '🔌 Conectar clientes']]
            .map(([id, txt]) => (
              <button key={id} type="button" onClick={() => setVista(id)}
                style={{ ...s.pest, ...(vista === id ? s.pestOn : {}) }}>{txt}</button>
            ))}
        </div>

        {cargando && !resumen && <div style={s.sub}>Leyendo la red…</div>}

        {vista === 'resumen' && resumen && (
          <Resumen resumen={resumen} arbol={arbol} totalSitios={totalSitios}
                   ubicados={ubicados} fibra={fibra} />
        )}
        {vista === 'mapa'   && mapa   && <Mapa datos={mapa} />}
        {vista === 'arbol'  && arbol  && <Arbol datos={arbol} abierto={abierto} setAbierto={setAbierto} />}
        {vista === 'sitios' && <Sitios sitios={sitios} alRecargar={cargar} />}
        {vista === 'cargar' && <Conectar sitios={sitios} alConectar={cargar} />}
      </div>
    </Layout>
  );
}

/* ─────────────────────────── Resumen ─────────────────────────── */

function Resumen({ resumen, arbol, totalSitios, ubicados, fibra }) {
  const eq = resumen.equipos || {};
  const pon = resumen.pon || [];
  const ponUsadas = pon.filter(p => p.clientes > 0).length;

  return (
    <>
      <div style={s.tarjetas}>
        <div style={s.tarjeta}>
          <div style={s.rotulo}>Sitios</div>
          <div style={{ ...s.cifra, color: COLOR.acento }}>{totalSitios}</div>
          <div style={s.pie}>
            {ubicados} con ubicación
            {totalSitios > ubicados && <span style={{ color: COLOR.aviso }}> · {totalSitios - ubicados} sin ubicar</span>}
          </div>
        </div>
        <div style={s.tarjeta}>
          <div style={s.rotulo}>Equipos</div>
          <div style={{ ...s.cifra, color: COLOR.azul }}>{eq.total || 0}</div>
          <div style={s.pie}>
            {eq.en_sitio || 0} en sitio · {eq.en_cliente || 0} en cliente
          </div>
        </div>
        <div style={s.tarjeta}>
          <div style={s.rotulo}>Clientes de fibra</div>
          <div style={{ ...s.cifra, color: COLOR.ok }}>{fibra?.total || 0}</div>
          <div style={s.pie}>
            {fibra?.conectados || 0} colgados de un sitio
            {fibra && fibra.total > fibra.conectados &&
              <span style={{ color: COLOR.aviso }}> · {fibra.total - fibra.conectados} sin conectar</span>}
          </div>
        </div>
        <div style={s.tarjeta}>
          <div style={s.rotulo}>PON en uso</div>
          <div style={{ ...s.cifra, color: ponUsadas ? COLOR.ok : COLOR.frio }}>{ponUsadas} / {pon.length}</div>
          <div style={s.pie}>hilos con clientes colgando</div>
        </div>
      </div>

      {/* Los PON son la columna vertebral de una red de fibra: por ahí sale todo. */}
      <div style={s.panel}>
        <div style={s.tituloP}>Los hilos que salen de la OLT</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={s.tabla}>
            <thead><tr>
              <th style={s.th}>PON</th><th style={s.th}>Color del hilo</th>
              <th style={s.th}>Para qué es</th><th style={s.th}>Estado</th><th style={s.th}>Clientes</th>
            </tr></thead>
            <tbody>
              {pon.map(p => (
                <tr key={p.numero}>
                  <td style={{ ...s.td, fontWeight: 700 }}>{p.numero}</td>
                  <td style={s.td}>{p.color_hilo || '—'}</td>
                  <td style={{ ...s.td, color: 'rgba(255,255,255,0.65)' }}>{p.descripcion || '—'}</td>
                  <td style={s.td}>
                    {p.estado === 'libre' ? pill('libre', COLOR.frio) : pill(p.estado || '—', COLOR.ok)}
                  </td>
                  <td style={{ ...s.td, fontWeight: 700,
                               color: p.clientes ? COLOR.ok : 'rgba(255,255,255,0.35)' }}>{p.clientes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div style={s.panel}>
        <div style={s.tituloP}>Clientes por tecnología</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={s.tabla}>
            <thead><tr>
              <th style={s.th}>Tecnología</th><th style={s.th}>Total</th>
              <th style={s.th}>Ubicados</th><th style={s.th}>Conectados a un sitio</th>
            </tr></thead>
            <tbody>
              {(resumen.clientes || []).map(c => (
                <tr key={c.tecnologia}>
                  <td style={{ ...s.td, fontWeight: 600 }}>{c.tecnologia}</td>
                  <td style={s.td}>{c.total}</td>
                  <td style={s.td}>{c.con_ubicacion}</td>
                  <td style={s.td}>{c.conectados}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Faltantes arbol={arbol} resumen={resumen} />
    </>
  );
}

/**
 * Lo que falta cargar, dicho con nombre y con el porqué.
 *
 * Un cero mudo parece un error del sistema. Estas tres tablas están vacías de verdad y sin
 * ellas el mapa y el árbol salen a medias, así que se dice qué son y para qué sirven.
 */
function Faltantes({ arbol, resumen }) {
  const sinUbicar = resumen?.sitios_sin_ubicar || 0;
  const sueltos = arbol?.cuentas || {};
  const hayAlgo = sinUbicar > 0 || sueltos.equipos_sueltos > 0 || sueltos.clientes_sueltos > 0;
  if (!hayAlgo) return null;

  return (
    <div style={s.aviso}>
      <b>Lo que falta para que el mapa y el árbol queden completos</b>
      <ul style={{ margin: '8px 0 0', paddingLeft: '18px' }}>
        {sinUbicar > 0 && (
          <li><b>{sinUbicar} sitios sin coordenadas.</b> No salen en el mapa. Se ubican desde
              cada sitio, o tomando la foto en terreno.</li>
        )}
        {resumen?.equipos_sin_atribuir > 0 && (
          <li><b>{resumen.equipos_sin_atribuir} equipos sin sitio ni cliente.</b> Están
              inventariados pero no se sabe dónde están colgados, así que no se puede decir si
              son de fibra o de radio — por eso no entran en el conteo de arriba.</li>
        )}
        {sueltos.clientes_sueltos > 0 && (
          <li><b>{sueltos.clientes_sueltos} clientes sin sitio</b> (de {sueltos.clientes_totales}).
              No se puede saber por qué caja les llega la fibra.</li>
        )}
      </ul>
    </div>
  );
}

/* ─────────────────────────── Mapa ─────────────────────────── */

/**
 * El mapa de la planta. Leaflet se carga solo cuando se abre esta pestaña: son ~150 KB que no
 * tienen por qué pesar en el resto del sistema.
 */
function Mapa({ datos }) {
  const caja = useRef(null);
  const mapaRef = useRef(null);
  const capasRef = useRef({});
  const [capa, setCapa] = useState('satelite');
  const [listo, setListo] = useState(false);

  useEffect(() => {
    let vivo = true;
    (async () => {
      const L = (await import('leaflet')).default;
      await import('leaflet/dist/leaflet.css');
      if (!vivo || !caja.current || mapaRef.current) return;

      const conUbicacion = (datos.sitios || []).filter(x => x.latitud && x.longitud);
      const centro = conUbicacion.length
        ? [Number(conUbicacion[0].latitud), Number(conUbicacion[0].longitud)]
        : [3.0089, -76.4847];                       // Santander de Quilichao
      const m = L.map(caja.current).setView(centro, conUbicacion.length ? 14 : 12);
      ponerCapa(L, m, capa, capasRef.current);
      L.tileLayer(ETIQUETAS.url, { maxZoom: ETIQUETAS.maxZoom, opacity: 0.9 }).addTo(m);

      // Los tramos primero, para que queden DEBAJO de los puntos.
      for (const t of (datos.tramos || [])) {
        if (!t.lat_origen || !t.lat_destino) continue;
        L.polyline([[Number(t.lat_origen), Number(t.lon_origen)],
                    [Number(t.lat_destino), Number(t.lon_destino)]],
                   { color: COLOR.acento, weight: 3, opacity: 0.75 })
         .bindPopup(`<b>${t.nombre_origen || ''} → ${t.nombre_destino || ''}</b>`)
         .addTo(m);
      }

      for (const x of conUbicacion) {
        const icono = ICONO_SITIO[String(x.tipo || '').toLowerCase()] || '📍';
        L.marker([Number(x.latitud), Number(x.longitud)], {
          icon: L.divIcon({
            className: '',
            html: `<div style="font-size:20px;line-height:1;text-shadow:0 0 4px #000">${icono}</div>`,
            iconSize: [22, 22], iconAnchor: [11, 11],
          }),
        }).bindPopup(
          `<b>${x.nombre || ''}</b><br>${x.tipo || ''}` +
          (x.pon ? `<br>PON ${x.pon}` : '') +
          (x.splitters ? `<br>Splitters: ${x.splitters}` : '')
        ).addTo(m);
      }

      for (const c of (datos.clientes || [])) {
        if (!c.latitud || !c.longitud) continue;
        L.circleMarker([Number(c.latitud), Number(c.longitud)], {
          radius: 4, weight: 1, color: '#fff',
          fillColor: c.estado === 'activo' ? COLOR.ok : COLOR.frio, fillOpacity: 0.95,
        }).bindPopup(`<b>${c.nombre || ''}</b><br>${c.estado || ''}`).addTo(m);
      }

      mapaRef.current = m;
      setListo(true);
    })();
    return () => { vivo = false; if (mapaRef.current) { mapaRef.current.remove(); mapaRef.current = null; } };
    // El mapa se arma UNA vez con los datos que llegaron; cambiar de capa no lo rehace, que
    // para eso está `cambiarCapa`. Por eso `capa` no va en las dependencias.
    //
    // Ojo: aquí NO va un `eslint-disable` de react-hooks. Este proyecto no tiene ese
    // complemento instalado, así que nombrar una regla que no existe es un error y la
    // compilación se cae — pasó al escribir esta pantalla.
  }, [datos]);

  const cambiarCapa = async (cual) => {
    setCapa(cual);
    if (!mapaRef.current) return;
    const L = (await import('leaflet')).default;
    ponerCapa(L, mapaRef.current, cual, capasRef.current);
  };

  const sinUbicar = (datos.sitios_por_ubicar || []).length;
  const sinDibujar = (datos.tramos_sin_dibujar || []).length;

  return (
    <>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
        {Object.entries(CAPAS).map(([id, c]) => (
          <button key={id} type="button" onClick={() => cambiarCapa(id)}
            style={{ ...s.pest, ...(capa === id ? s.pestOn : {}) }}>{c.nombre}</button>
        ))}
        <div style={{ ...s.sub, marginLeft: 'auto', alignSelf: 'center' }}>
          {(datos.sitios || []).filter(x => x.latitud).length} sitios · {(datos.tramos || []).length} tramos
        </div>
      </div>
      <div ref={caja} style={s.mapa} />
      {!listo && <div style={s.sub}>Cargando el mapa…</div>}

      {(sinUbicar > 0 || sinDibujar > 0) && (
        <div style={s.aviso}>
          {sinUbicar > 0 && <div><b>{sinUbicar} sitios no salen</b> porque no tienen coordenadas: {
            (datos.sitios_por_ubicar || []).slice(0, 6).join(', ')}{sinUbicar > 6 ? '…' : ''}</div>}
          {sinDibujar > 0 && <div style={{ marginTop: '6px' }}>
            <b>{sinDibujar} tramos no se pueden dibujar</b> porque les falta ubicación en una
            de las dos puntas.</div>}
        </div>
      )}
    </>
  );
}

/* ─────────────────────────── Árbol ─────────────────────────── */

/**
 * De dónde cuelga cada cosa. Solo la parte de fibra: el motor devuelve también el radio, y
 * mezclarlos llenaría la pantalla de lo que no se pidió.
 */
function Arbol({ datos, abierto, setAbierto }) {
  const raices = datos.arbol || [];
  const alternar = (id) => setAbierto(a => ({ ...a, [id]: !a[id] }));

  if (!raices.length) {
    return <div style={s.aviso}>
      Todavía no hay nada colgado de la OLT. Los sitios existen, pero no se ha dicho cuál
      cuelga de cuál — eso son los <b>tramos</b>, y están en cero.
    </div>;
  }

  const Nodo = ({ n, nivel }) => {
    const hijos = n.hijos || [];
    const clientes = n.clientes || [];
    const abre = abierto[n.id] !== false;   // por defecto abierto: se abre para ver, no para esconder
    const icono = ICONO_SITIO[String(n.tipo || '').toLowerCase()] || '📍';
    return (
      <div style={{ marginLeft: nivel ? '18px' : 0,
                    borderLeft: nivel ? '1px solid rgba(255,255,255,0.1)' : 'none',
                    paddingLeft: nivel ? '12px' : 0 }}>
        <div onClick={() => alternar(n.id)}
             style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 4px',
                      cursor: (hijos.length || clientes.length) ? 'pointer' : 'default' }}>
          <span style={{ width: '12px', color: 'rgba(255,255,255,0.4)', fontSize: '11px' }}>
            {(hijos.length || clientes.length) ? (abre ? '▾' : '▸') : ''}
          </span>
          <span style={{ fontSize: '15px' }}>{icono}</span>
          <span style={{ fontSize: '13.5px', fontWeight: 600 }}>{n.nombre}</span>
          {n.pon ? pill('PON ' + n.pon, COLOR.acento) : null}
          {clientes.length ? pill(clientes.length + (clientes.length === 1 ? ' cliente' : ' clientes'), COLOR.ok) : null}
        </div>
        {abre && (
          <>
            {hijos.map(h => <Nodo key={h.id} n={h} nivel={nivel + 1} />)}
            {clientes.length > 0 && (
              <div style={{ marginLeft: '30px', paddingLeft: '12px',
                            borderLeft: '1px solid rgba(255,255,255,0.07)' }}>
                {clientes.map(c => (
                  <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '7px',
                                           padding: '4px 0', fontSize: '12.5px',
                                           color: 'rgba(255,255,255,0.7)' }}>
                    <span style={{ width: '6px', height: '6px', borderRadius: '50%',
                                   background: c.estado === 'activo' ? COLOR.ok : COLOR.frio }} />
                    {c.nombre}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div style={s.panel}>
      {raices.map(r => <Nodo key={r.id} n={r} nivel={0} />)}
    </div>
  );
}

/* ─────────────────────────── Sitios ─────────────────────────── */

function Sitios({ sitios, alRecargar }) {
  const [busca, setBusca] = useState('');
  /** null = ficha cerrada · {} = sitio nuevo · {…} = editando uno que existe */
  const [ficha, setFicha] = useState(null);
  const lista = sitios.filter(x =>
    !busca || String(x.nombre || '').toLowerCase().includes(busca.toLowerCase()));

  return (
    <>
      {ficha && (
        <FichaSitio sitio={ficha} sitios={sitios}
          alCerrar={() => setFicha(null)}
          alGuardar={async () => { setFicha(null); if (alRecargar) await alRecargar(); }} />
      )}

      <div style={{ display: 'flex', gap: '9px', flexWrap: 'wrap', alignItems: 'center' }}>
        <input value={busca} onChange={e => setBusca(e.target.value)}
          placeholder="Buscar un sitio por nombre…" style={{ ...s.input, flex: '1 1 260px', minWidth: 0 }} />
        <button type="button" onClick={() => setFicha({})}
          style={{ ...s.pest, ...s.pestOn }}>+ Nuevo sitio</button>
      </div>
      <div style={s.panel}>
        <div style={{ overflowX: 'auto' }}>
          <table style={s.tabla}>
            <thead><tr>
              <th style={s.th}>Sitio</th><th style={s.th}>Tipo</th><th style={s.th}>PON</th>
              <th style={s.th}>Cuelga de</th>
              <th style={s.th}>Ubicación</th><th style={s.th}>Estado</th><th style={s.th}> </th>
            </tr></thead>
            <tbody>
              {lista.map(x => (
                <tr key={x.id} onClick={() => setFicha(x)} style={{ cursor: 'pointer' }}>
                  <td style={{ ...s.td, fontWeight: 600 }}>
                    {ICONO_SITIO[String(x.tipo || '').toLowerCase()] || '📍'} {x.nombre}
                    {x.principal ? ' ' : ''}{x.principal ? pill('principal', COLOR.acento) : null}
                  </td>
                  <td style={{ ...s.td, color: 'rgba(255,255,255,0.6)' }}>{x.tipo || '—'}</td>
                  <td style={s.td}>{x.pon || '—'}</td>
                  {/* De qué sitio cuelga: es lo que dibuja el árbol y el mapa, así que se ve
                      en la lista y no escondido dentro de la ficha. */}
                  <td style={{ ...s.td, color: 'rgba(255,255,255,0.6)' }}>
                    {x.padre_nombre || <span style={{ color: COLOR.aviso }}>de nada</span>}
                  </td>
                  <td style={s.td}>
                    {x.latitud ? pill('ubicado', COLOR.ok) : pill('sin ubicar', COLOR.aviso)}
                  </td>
                  <td style={{ ...s.td, color: 'rgba(255,255,255,0.6)' }}>{x.estado || '—'}</td>
                  <td style={{ ...s.td, color: COLOR.acento, fontSize: '12px' }}>editar ›</td>
                </tr>
              ))}
              {!lista.length && (
                <tr><td style={{ ...s.td, color: 'rgba(255,255,255,0.4)' }} colSpan={7}>
                  Ningún sitio con ese nombre.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

/* ─────────────────────────── Ficha de un sitio ─────────────────────────── */

/**
 * Los tipos que se ofrecen al crear o editar.
 *
 * **Sin «poste», por decisión de Víctor (24-sep-2026): no los usa.** El tipo sigue siendo
 * válido en el servidor y en la clasificación fibra/radio —hay prueba que exige que todo tipo
 * esté en una de las dos redes— pero no se ofrece aquí. Y si un sitio YA es de un tipo que no
 * está en esta lista, se le añade su tipo actual: editarle el nombre no puede cambiarle la
 * naturaleza a escondidas.
 */
const TIPOS_QUE_SE_OFRECEN = [
  ['olt', '🏢 OLT'],
  ['mufla', '🔗 Mufla'],
  ['caja', '📦 Caja'],
];

const COLORES_HILO = ['Azul', 'Naranja', 'Verde', 'Café', 'Gris', 'Blanco', 'Rojo', 'Negro',
                      'Amarillo', 'Violeta', 'Rosado', 'Aguamarina'];

const fondoModal = {
  position: 'fixed', inset: 0, background: 'rgba(3,6,12,0.78)', zIndex: 300,
  display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
  padding: '24px 14px', overflowY: 'auto',
};
const cajaModal = {
  background: '#0F1524', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '14px',
  width: '100%', maxWidth: '720px', padding: '18px 20px 22px', color: '#fff',
};
const campo = { marginBottom: '11px' };
const etiqueta = { fontSize: '11.5px', color: 'rgba(255,255,255,0.5)', marginBottom: '4px',
                   fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px' };

/**
 * Crear un sitio o cambiarle lo que sea, sin salir de la pantalla de red.
 *
 * ── Qué resuelve ──
 *
 * Hasta ahora un sitio solo se podía crear con nombre y tipo —desde «Conectar clientes»— y NO
 * se podía editar por ningún lado. Lo más grave es que **no se podía cambiar de qué sitio
 * cuelga**, que es justo lo que dibuja el árbol y el mapa: una caja mal colgada se veía mal
 * para siempre. Víctor lo pidió así: «debe poderse permitir crear y modificar independiente».
 *
 * ── Lo que NO hace, a propósito ──
 *
 * No borra un sitio que tenga algo colgando. Eso lo impide el servidor y aquí solo se enseña
 * su explicación: si se borrara, los clientes quedarían sin conexión conocida y nadie se
 * enteraría hasta que uno llamara.
 */

/* La cámara de un celular devuelve 4-8 MB y el servidor corta en 3. Se encoge a 1600 px de
   lado mayor y se recomprime a JPG, que es lo que ya cabía en los 200-400 KB que el
   controlador da por supuestos. Sin esto, tomar la foto en terreno falla justo cuando se
   está parado delante de la caja, que es cuando más caro sale. */
const encogerFoto = (archivo, lado = 1600, calidad = 0.82) => new Promise((listo, falla) => {
  const lector = new FileReader();
  lector.onerror = () => falla(new Error('No se pudo leer la foto'));
  lector.onload = () => {
    const img = new Image();
    img.onerror = () => falla(new Error('Ese archivo no es una imagen'));
    img.onload = () => {
      const escala = Math.min(1, lado / Math.max(img.width, img.height));
      const lienzo = document.createElement('canvas');
      lienzo.width = Math.round(img.width * escala);
      lienzo.height = Math.round(img.height * escala);
      lienzo.getContext('2d').drawImage(img, 0, 0, lienzo.width, lienzo.height);
      listo({ base64: lienzo.toDataURL('image/jpeg', calidad), mime: 'image/jpeg' });
    };
    img.src = lector.result;
  };
  lector.readAsDataURL(archivo);
});

function FichaSitio({ sitio, sitios, alCerrar, alGuardar }) {
  const nuevo = !sitio?.id;
  const [f, setF] = useState({
    nombre: sitio?.nombre || '', tipo: sitio?.tipo || 'caja',
    padre_id: sitio?.padre_id || '', pon: sitio?.pon || '',
    color_hilo: sitio?.color_hilo || '', latitud: sitio?.latitud ?? '',
    longitud: sitio?.longitud ?? '', capacidad: sitio?.capacidad ?? '',
    estado: sitio?.estado || 'activo', direccion: sitio?.direccion || '',
    notas: sitio?.notas || '',
    /* Si no viaja, el servidor no sabe si el punto se midió o se señaló, y quien salga a
       buscar la caja no distingue un punto exacto de uno aproximado. */
    ubicacion_origen: sitio?.ubicacion_origen || '',
  });
  const [detalle, setDetalle] = useState(null);
  /* Los splitters y los ratios validos vienen de su propio endpoint: `/puertos` no los trae.
     Tomar los ratios del servidor y no de una lista escrita aqui evita que un ratio nuevo en
     la base quede invisible en la pantalla. */
  const [splits, setSplits] = useState(null);
  const [ratio, setRatio] = useState('1x8');
  const [cuantos, setCuantos] = useState('1');
  const [alimenta, setAlimenta] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [fallo, setFallo] = useState('');

  /* Ubicación: señalarla en el mapa desde el escritorio, o tomarla con el GPS estando allá.
     Se guarda CUÁL de las dos fue (`ubicacion_origen`), porque quien salga a buscar esa caja
     necesita saber si el punto es exacto o aproximado. */
  const [enMapa, setEnMapa] = useState(false);
  const [buscandoGPS, setBuscandoGPS] = useState(false);
  const [precision, setPrecision] = useState(null);

  /* Fotos. En un sitio que ya existe se suben al momento. En uno NUEVO todavía no hay id al
     que colgarlas, así que esperan aquí y suben solas en cuanto el sitio queda creado — que
     es lo que pidió Víctor: poder tomarlas a la hora de agregarlo, no volver después. */
  const [fotos, setFotos] = useState([]);
  const [enEspera, setEnEspera] = useState([]);
  const [subiendo, setSubiendo] = useState(false);

  const poner = (k, v) => setF(x => ({ ...x, [k]: v }));

  const tomarGPS = () => {
    if (!navigator.geolocation) return setFallo('Este aparato no tiene GPS');
    setBuscandoGPS(true); setFallo('');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setF(x => ({ ...x, latitud: pos.coords.latitude.toFixed(6),
                           longitud: pos.coords.longitude.toFixed(6),
                           ubicacion_origen: 'gps' }));
        setPrecision(Math.round(pos.coords.accuracy));
        setBuscandoGPS(false);
      },
      (e) => {
        /* El mensaje del navegador es «User denied Geolocation» y no dice qué hacer. */
        setFallo(e.code === 1
          ? 'El navegador no dejó usar la ubicación. Hay que darle permiso en el candado de la barra de direcciones.'
          : 'No se pudo tomar la ubicación. Intenta al aire libre.');
        setBuscandoGPS(false);
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 });
  };

  const traerFotos = useCallback(async () => {
    if (nuevo) return;
    try { setFotos((await api.get(`/api/red/sitios/${sitio.id}/fotos`)).data); }
    catch (e) { /* la ficha sirve igual sin las fotos */ }
  }, [nuevo, sitio]);

  const subirUna = async (sitioId, foto) =>
    api.post(`/api/red/sitios/${sitioId}/fotos`, {
      imagen_base64: foto.base64, imagen_mime: foto.mime, origen: foto.origen,
      latitud: f.latitud === '' ? null : Number(f.latitud),
      longitud: f.longitud === '' ? null : Number(f.longitud),
    });

  const agregarFotos = async (e) => {
    const archivos = [...e.target.files];
    e.target.value = '';               // para poder volver a escoger la misma foto
    if (!archivos.length) return;
    setSubiendo(true); setFallo('');
    try {
      for (const a of archivos) {
        const { base64, mime } = await encogerFoto(a);
        /* `capture` abre la cámara, pero el usuario puede escoger de la galería igual: lo
           que de verdad distingue una foto tomada ahí es que el archivo acaba de nacer. */
        const origen = Date.now() - a.lastModified < 60000 ? 'camara' : 'subida';
        if (nuevo) setEnEspera(x => [...x, { base64, mime, origen, nombre: a.name }]);
        else { await subirUna(sitio.id, { base64, mime, origen }); }
      }
      if (!nuevo) await traerFotos();
    } catch (err) {
      setFallo(err.response?.data?.error || err.message || 'No se pudo guardar la foto');
    } finally { setSubiendo(false); }
  };

  const borrarFoto = async (id) => {
    if (!window.confirm('¿Borrar esta foto?')) return;
    try { await api.delete(`/api/red/fotos/${id}`); await traerFotos(); }
    catch (e) { setFallo(e.response?.data?.error || 'No se pudo borrar'); }
  };

  const [mirando, setMirando] = useState(null);
  const verFoto = async (x) => {
    setMirando({ cargando: true });
    try {
      const { data } = await api.get(`/api/red/fotos/${x.id}`);
      setMirando({ src: `data:${data.imagen_mime};base64,${data.imagen_base64}`, info: x });
    } catch (e) {
      setMirando(null);
      setFallo(e.response?.data?.error || 'No se pudo abrir la foto');
    }
  };

  const traerDetalle = useCallback(async () => {
    if (nuevo) return;
    try {
      const [d, sp] = await Promise.all([
        api.get(`/api/red/sitios/${sitio.id}/puertos`),
        api.get(`/api/red/sitios/${sitio.id}/splitters`),
      ]);
      setDetalle(d.data); setSplits(sp.data);
    } catch (e) { /* la ficha sirve igual sin el detalle: los campos se editan */ }
  }, [nuevo, sitio]);

  useEffect(() => { traerDetalle(); }, [traerDetalle]);
  useEffect(() => { traerFotos(); }, [traerFotos]);

  const guardar = async () => {
    if (!f.nombre.trim()) return setFallo('Ponle un nombre');
    setOcupado(true); setFallo('');
    const cuerpo = {
      ...f,
      padre_id: f.padre_id || null,
      pon: f.pon === '' ? null : Number(f.pon),
      capacidad: f.capacidad === '' ? null : Number(f.capacidad),
      latitud: f.latitud === '' ? null : Number(f.latitud),
      longitud: f.longitud === '' ? null : Number(f.longitud),
    };
    try {
      let id = sitio?.id;
      if (nuevo) id = (await api.post('/api/red/sitios', cuerpo)).data.id;
      else await api.put(`/api/red/sitios/${sitio.id}`, cuerpo);

      /* Las fotos que se tomaron mientras se llenaba el formulario suben ahora, que ya hay
         a qué colgarlas. Si alguna falla, el sitio YA quedó creado: se dice cuántas no
         subieron en vez de callarlo, porque el sitio se ve bien y la foto no está. */
      if (enEspera.length) {
        let fallaron = 0;
        for (const foto of enEspera) {
          try { await subirUna(id, foto); } catch (e) { fallaron++; }
        }
        setEnEspera([]);
        if (fallaron) {
          setFallo(`El sitio quedó creado, pero ${fallaron} de ${enEspera.length} fotos no subieron. Ábrelo y vuelve a agregarlas.`);
          setOcupado(false);
          return;
        }
      }
      await alGuardar();
    } catch (e) { setFallo(e.response?.data?.error || 'No se pudo guardar'); }
    finally { setOcupado(false); }
  };

  const eliminar = async () => {
    if (!window.confirm(`¿Eliminar «${f.nombre}»? Esto no se puede deshacer.`)) return;
    setOcupado(true); setFallo('');
    try {
      await api.delete(`/api/red/sitios/${sitio.id}`);
      await alGuardar();
    } catch (e) {
      // El servidor explica QUÉ cuelga de él; se enseña tal cual porque es accionable.
      setFallo(e.response?.data?.error || 'No se pudo eliminar');
    } finally { setOcupado(false); }
  };

  const agregarSplitter = async () => {
    setOcupado(true); setFallo('');
    try {
      await api.post(`/api/red/sitios/${sitio.id}/splitters`,
        { ratio, cantidad: Number(cuantos) || 1, alimenta_otros: alimenta });
      await traerDetalle();
    } catch (e) { setFallo(e.response?.data?.error || 'No se pudo agregar el splitter'); }
    finally { setOcupado(false); }
  };

  const quitarSplitter = async (id) => {
    setOcupado(true); setFallo('');
    try {
      await api.delete(`/api/red/splitters/${id}`);
      await traerDetalle();
    } catch (e) { setFallo(e.response?.data?.error || 'No se pudo quitar'); }
    finally { setOcupado(false); }
  };

  const moverCliente = async (clienteId, destino) => {
    setOcupado(true); setFallo('');
    try {
      await api.put(`/api/red/clientes/${clienteId}/conectar`, { sitio_id: destino || null });
      await traerDetalle();
      await alGuardar();
    } catch (e) { setFallo(e.response?.data?.error || 'No se pudo mover'); }
    finally { setOcupado(false); }
  };

  /* Un sitio no puede colgar de sí mismo. Los hijos se dejan: cambiar el orden de una rama es
     legítimo, y el servidor es quien tiene la foto completa para impedir un círculo. */
  const posiblesPadres = sitios.filter(x => x.id !== sitio?.id);
  const tipos = TIPOS_QUE_SE_OFRECEN.some(([t]) => t === f.tipo)
    ? TIPOS_QUE_SE_OFRECEN
    : [...TIPOS_QUE_SE_OFRECEN, [f.tipo, f.tipo]];

  const clientes = detalle?.sitio ? (detalle.puertos || [])
    .map(p => p.ocupante).filter(o => o && o.clase === 'cliente') : [];
  const sueltos = detalle?.sin_puerto || [];
  const todosLosClientes = [...clientes, ...sueltos];

  return (
    <div style={fondoModal} onClick={(e) => { if (e.target === e.currentTarget) alCerrar(); }}>
      <div style={cajaModal}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
          <h2 style={{ ...s.h1, fontSize: '17px', flex: 1 }}>
            {nuevo ? 'Nuevo sitio de fibra' : `${ICONO_SITIO[f.tipo] || '📍'} ${sitio.nombre}`}
          </h2>
          <button type="button" onClick={alCerrar} style={s.pest}>Cerrar</button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
                      gap: '0 12px' }}>
          <div style={campo}>
            <div style={etiqueta}>Nombre</div>
            <input value={f.nombre} onChange={(e) => poner('nombre', e.target.value)}
              style={{ ...s.input, width: '100%' }} />
          </div>
          <div style={campo}>
            <div style={etiqueta}>Qué es</div>
            <select value={f.tipo} onChange={(e) => poner('tipo', e.target.value)}
              style={{ ...s.input, width: '100%' }}>
              {tipos.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
            </select>
          </div>
          <div style={{ ...campo, gridColumn: '1 / -1' }}>
            <div style={etiqueta}>De qué sitio cuelga — esto dibuja el árbol y el mapa</div>
            <select value={f.padre_id || ''} onChange={(e) => poner('padre_id', e.target.value)}
              style={{ ...s.input, width: '100%' }}>
              <option value="">De nada (es la raíz)</option>
              {posiblesPadres.map(x => (
                <option key={x.id} value={x.id}>
                  {x.nombre}{x.tipo ? ` · ${x.tipo}` : ''}{x.pon ? ` · PON ${x.pon}` : ''}
                </option>
              ))}
            </select>
          </div>
          <div style={campo}>
            <div style={etiqueta}>PON (hilo de la OLT)</div>
            <select value={f.pon || ''} onChange={(e) => poner('pon', e.target.value)}
              style={{ ...s.input, width: '100%' }}>
              <option value="">Sin definir</option>
              {[1, 2, 3, 4, 5, 6, 7, 8].map(n => <option key={n} value={n}>PON {n}</option>)}
            </select>
          </div>
          <div style={campo}>
            <div style={etiqueta}>Color del hilo</div>
            <select value={f.color_hilo || ''} onChange={(e) => poner('color_hilo', e.target.value)}
              style={{ ...s.input, width: '100%' }}>
              <option value="">Sin definir</option>
              {COLORES_HILO.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          {/* Antes aquí había dos casillas sueltas, «Latitud» y «Longitud», para escribir a
              mano unos números que nadie se sabe de memoria: en la práctica los sitios se
              quedaban sin ubicar. Se usan los dos caminos que ya existen en Clientes —
              señalar en el mapa desde el escritorio, o tomar el GPS estando allá— y se
              guarda cuál de los dos fue, porque un punto exacto y uno aproximado no se
              buscan igual. Las casillas siguen, por si hay que corregir un número. */}
          <div style={{ ...campo, gridColumn: '1 / -1', background: 'rgba(16,185,129,0.04)',
                        border: '1px solid rgba(16,185,129,0.12)', borderRadius: '10px', padding: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
              <div style={{ ...etiqueta, color: '#10B981', fontWeight: '600', margin: 0 }}>
                📍 Dónde está {f.latitud ? '' : '— sin ubicar'}
              </div>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                <button type="button" onClick={() => setEnMapa(true)}
                  style={{ background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.35)',
                           borderRadius: '8px', padding: '6px 12px', color: '#A5B4FC',
                           fontSize: '12px', cursor: 'pointer' }}>
                  🗺️ Señalar en el mapa
                </button>
                <button type="button" onClick={tomarGPS} disabled={buscandoGPS}
                  style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.3)',
                           borderRadius: '8px', padding: '6px 12px', color: '#6EE7B7', fontSize: '12px',
                           cursor: buscandoGPS ? 'wait' : 'pointer', opacity: buscandoGPS ? 0.6 : 1 }}>
                  {buscandoGPS ? '📡 Buscando señal…' : '📡 Estoy en el sitio'}
                </button>
              </div>
            </div>
            {f.ubicacion_origen === 'mapa' && f.latitud && (
              <div style={{ fontSize: '11.5px', color: '#FDBA74', marginBottom: '8px' }}>
                Señalado en el mapa: es aproximado. Quien vaya al sitio puede corregirlo con «Estoy en el sitio».
              </div>
            )}
            {precision != null && !buscandoGPS && (
              <div style={{ fontSize: '11.5px', marginBottom: '8px',
                            color: precision <= 30 ? '#6EE7B7' : '#FBBF24' }}>
                Tomado con el GPS, precisión {precision} m{precision > 100 ? ' — poco exacta, intenta al aire libre' : ''}
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <div>
                <div style={etiqueta}>Latitud</div>
                <input value={f.latitud} onChange={(e) => poner('latitud', e.target.value)}
                  placeholder="3.0089" style={{ ...s.input, width: '100%' }} />
              </div>
              <div>
                <div style={etiqueta}>Longitud</div>
                <input value={f.longitud} onChange={(e) => poner('longitud', e.target.value)}
                  placeholder="-76.4847" style={{ ...s.input, width: '100%' }} />
              </div>
            </div>
          </div>

          {/* Las fotos del sitio. El servidor ya sabía guardarlas desde hace meses —subir,
              listar y borrar— y la pantalla no las pedía por ningún lado. En un sitio nuevo
              esperan y suben solas al crearlo, para poder tomarlas estando delante de la caja. */}
          <div style={{ ...campo, gridColumn: '1 / -1' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
              <div style={{ ...etiqueta, margin: 0 }}>
                📷 Fotos {fotos.length + enEspera.length > 0 && `(${fotos.length + enEspera.length})`}
              </div>
              <label style={{ background: 'rgba(0,229,255,0.12)', border: '1px solid rgba(0,229,255,0.3)',
                              borderRadius: '8px', padding: '6px 12px', color: COLOR.acento,
                              fontSize: '12px', cursor: subiendo ? 'wait' : 'pointer',
                              opacity: subiendo ? 0.6 : 1 }}>
                {subiendo ? 'Guardando…' : '📷 Tomar o escoger'}
                <input type="file" accept="image/*" capture="environment" multiple
                  disabled={subiendo} onChange={agregarFotos} style={{ display: 'none' }} />
              </label>
            </div>
            {fotos.length + enEspera.length === 0 ? (
              <div style={{ fontSize: '11.5px', color: COLOR.frio }}>
                Sin fotos. Desde el celular el botón abre la cámara.
              </div>
            ) : (
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {enEspera.map((x, i) => (
                  <div key={`espera-${i}`} style={{ position: 'relative' }}>
                    <img src={x.base64} alt="" style={{ width: '72px', height: '72px',
                      objectFit: 'cover', borderRadius: '8px', opacity: 0.75,
                      border: '1px solid rgba(252,211,77,0.5)' }} />
                    <span style={{ position: 'absolute', bottom: '2px', left: '2px', right: '2px',
                      fontSize: '9px', textAlign: 'center', color: '#1F2937', borderRadius: '4px',
                      background: 'rgba(252,211,77,0.9)' }}>al guardar</span>
                  </div>
                ))}
                {/* La miniatura NO trae la imagen. El servidor separó «listar» de «obtener»
                    para que la ficha abra rápido aunque haya 12, y además la API pide token,
                    que un <img src="/api/…"> no manda. Se descarga al tocarla, que en la
                    calle también ahorra datos del celular. */}
                {fotos.map(x => (
                  <div key={x.id} style={{ position: 'relative' }}>
                    <button type="button" onClick={() => verFoto(x)}
                      title={`${x.kb} KB · ${new Date(x.creado_en).toLocaleDateString('es-CO')}${x.creado_por_nombre ? ' · ' + x.creado_por_nombre : ''}`}
                      style={{ width: '72px', height: '72px', borderRadius: '8px', cursor: 'pointer',
                               border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.04)',
                               color: COLOR.frio, fontSize: '10px', display: 'flex', gap: '2px',
                               flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ fontSize: '20px' }}>{x.origen === 'camara' ? '📷' : '🖼️'}</span>
                      <span>{new Date(x.creado_en).toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit' })}</span>
                      <span>{x.kb} KB</span>
                    </button>
                    <button type="button" onClick={() => borrarFoto(x.id)} title="Borrar"
                      style={{ position: 'absolute', top: '-6px', right: '-6px', width: '20px',
                               height: '20px', borderRadius: '50%', border: 'none', cursor: 'pointer',
                               background: COLOR.malo, color: '#fff', fontSize: '11px', lineHeight: 1 }}>×</button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div style={campo}>
            <div style={etiqueta}>Capacidad escrita a mano</div>
            <input value={f.capacidad} onChange={(e) => poner('capacidad', e.target.value.replace(/[^0-9]/g, ''))}
              placeholder="sale de los splitters" style={{ ...s.input, width: '100%' }} />
          </div>
          <div style={campo}>
            <div style={etiqueta}>Estado</div>
            <select value={f.estado} onChange={(e) => poner('estado', e.target.value)}
              style={{ ...s.input, width: '100%' }}>
              <option value="activo">Activo</option>
              <option value="planeado">Planeado</option>
              <option value="retirado">Retirado</option>
            </select>
          </div>
          <div style={{ ...campo, gridColumn: '1 / -1' }}>
            <div style={etiqueta}>Dirección o referencia</div>
            <input value={f.direccion} onChange={(e) => poner('direccion', e.target.value)}
              placeholder="Frente a la cancha, poste esquinero…"
              style={{ ...s.input, width: '100%' }} />
          </div>
          <div style={{ ...campo, gridColumn: '1 / -1' }}>
            <div style={etiqueta}>Notas</div>
            <textarea value={f.notas} onChange={(e) => poner('notas', e.target.value)} rows={2}
              style={{ ...s.input, width: '100%', resize: 'vertical' }} />
          </div>
        </div>

        {!nuevo && (
          <>
            <div style={{ ...s.panel, marginTop: '6px' }}>
              <div style={s.tituloP}>
                Splitters
                {detalle?.resumen?.capacidad_definida && (
                  <span style={{ fontWeight: 400, color: 'rgba(255,255,255,0.5)' }}>
                    {' '}— {detalle.resumen.ocupados} ocupados de {detalle.resumen.capacidad},
                    {' '}{detalle.resumen.libres} libres
                  </span>
                )}
              </div>
              {(splits?.splitters || []).length === 0 && (
                <div style={{ ...s.pie, marginTop: 0 }}>
                  Sin splitters anotados. Sin esto no se puede saber cuántos puertos libres quedan.
                </div>
              )}
              {(splits?.splitters || []).map((sp, i) => (
                <div key={sp.id || i} style={{ display: 'flex', alignItems: 'center', gap: '9px',
                                               padding: '6px 0', fontSize: '13px' }}>
                  <span style={{ fontWeight: 700 }}>{sp.cantidad > 1 ? `${sp.cantidad}× ` : ''}{sp.ratio}</span>
                  <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>
                    {sp.alimenta_otros ? 'alimenta otras cajas' : 'para clientes'}
                    {sp.salidas ? ` · ${sp.salidas * (sp.cantidad || 1)} salidas` : ''}
                  </span>
                  {sp.id && (
                    <button type="button" onClick={() => quitarSplitter(sp.id)} disabled={ocupado}
                      style={{ ...s.pest, marginLeft: 'auto', padding: '4px 10px', fontSize: '12px' }}>
                      Quitar
                    </button>
                  )}
                </div>
              ))}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '10px',
                            paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                <select value={ratio} onChange={(e) => setRatio(e.target.value)}
                  style={{ ...s.input, flex: '0 1 110px' }}>
                  {(splits?.ratios || []).map(r => (
                    <option key={r.ratio} value={r.ratio}>{r.ratio} · {r.salidas} salidas</option>
                  ))}
                </select>
                <input value={cuantos} onChange={(e) => setCuantos(e.target.value.replace(/[^0-9]/g, ''))}
                  style={{ ...s.input, width: '70px' }} />
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12.5px',
                                color: 'rgba(255,255,255,0.7)' }}>
                  <input type="checkbox" checked={alimenta} onChange={(e) => setAlimenta(e.target.checked)}
                    style={{ width: 'auto', accentColor: COLOR.acento }} />
                  alimenta otras cajas
                </label>
                <button type="button" onClick={agregarSplitter} disabled={ocupado}
                  style={{ ...s.pest, ...s.pestOn }}>+ Agregar</button>
              </div>
            </div>

            {todosLosClientes.length > 0 && (
              <div style={s.panel}>
                <div style={s.tituloP}>Clientes colgados aquí ({todosLosClientes.length})</div>
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {todosLosClientes.map(c => (
                    <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '8px',
                                             padding: '6px 0', fontSize: '13px',
                                             borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        {c.nombre}
                        {c.puerto != null && <span style={{ color: 'rgba(255,255,255,0.4)' }}> · puerto {c.puerto}</span>}
                      </span>
                      {/* Mover y desconectar son la misma acción: escoger otro destino, o ninguno. */}
                      <select defaultValue="" disabled={ocupado}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v !== '') moverCliente(c.id, v === 'ninguno' ? null : v);
                        }}
                        style={{ ...s.input, flex: '0 1 190px', fontSize: '12px', padding: '5px 8px' }}>
                        <option value="">Mover a…</option>
                        <option value="ninguno">— Desconectar —</option>
                        {posiblesPadres.map(x => <option key={x.id} value={x.id}>{x.nombre}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {fallo && <div style={{ ...s.aviso, color: '#FCA5A5', background: 'rgba(239,68,68,0.1)',
                                borderColor: 'rgba(239,68,68,0.35)' }}>{fallo}</div>}

        <div style={{ display: 'flex', gap: '9px', marginTop: '16px', flexWrap: 'wrap' }}>
          <button type="button" onClick={guardar} disabled={ocupado || !f.nombre.trim()}
            style={{ ...s.pest, flex: 1, padding: '11px',
                     background: 'rgba(34,197,94,0.18)', color: COLOR.ok,
                     borderColor: 'rgba(34,197,94,0.55)' }}>
            {ocupado ? 'Guardando…' : (nuevo ? 'Crear el sitio' : 'Guardar los cambios')}
          </button>
          {!nuevo && (
            <button type="button" onClick={eliminar} disabled={ocupado}
              style={{ ...s.pest, color: '#FCA5A5', borderColor: 'rgba(239,68,68,0.4)' }}>
              Eliminar
            </button>
          )}
        </div>
      </div>

      {enMapa && (
        <ElegirUbicacion
          inicial={f} nombre={f.nombre || 'este sitio'}
          alElegir={(p) => setF(x => ({ ...x, latitud: p.latitud, longitud: p.longitud,
                                             ubicacion_origen: p.ubicacion_origen }))}
          alCerrar={() => setEnMapa(false)} />
      )}

      {mirando && (
        <div onClick={() => setMirando(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 2200, background: 'rgba(0,0,0,0.85)',
                   display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          {mirando.cargando
            ? <div style={{ color: '#fff' }}>Abriendo la foto…</div>
            : <img src={mirando.src} alt={mirando.info?.descripcion || 'foto del sitio'}
                style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: '10px' }} />}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────── Conectar clientes ─────────────────────────── */

/**
 * Decir de qué caja cuelga cada cliente, SIN escribir nada a mano.
 *
 * ── Por qué se rehízo ──
 *
 * La primera versión era un recuadro de texto donde Víctor tenía que teclear el nombre de la
 * caja y el del cliente, y el sistema adivinaba a quién se refería. Él lo miró y dijo lo justo:
 * «¿esto te parece profesional? esperaba un diálogo con opciones predefinidas y automatizado».
 *
 * Tenía razón, y el error de fondo era tonto: **el sistema ya conoce sus 19 cajas y sus 115
 * clientes.** Pedirle que los escriba es devolverle el trabajo y, encima, abrir la puerta a
 * errores de dedo que después hay que emparejar por parecido. Si los datos están, se escogen.
 *
 * ── Cómo funciona ──
 *
 * Se escoge la caja de una lista, se marcan los clientes de otra, y se conectan. Nada que
 * teclear salvo el número del primer puerto, y eso porque numerar solo es lo que evita abrir
 * cliente por cliente: se marcan ocho, se dice que el primero va en el 3, y quedan del 3 al 10.
 *
 * ── Lo que se ve antes de escribir ──
 *
 * Cuántos se van a conectar y a qué caja, en el propio botón. No hace falta un paso de revisión
 * como el del texto pegado, porque aquí no hay nada que interpretar: lo que se marcó es lo que
 * se conecta.
 */
function Conectar({ sitios, alConectar }) {
  const [pendientes, setPendientes] = useState([]);
  const [marcados, setMarcados] = useState([]);
  const [caja, setCaja] = useState('');
  const [puerto, setPuerto] = useState('');
  const [busca, setBusca] = useState('');
  const [cargando, setCargando] = useState(true);
  const [ocupado, setOcupado] = useState(false);
  const [aviso, setAviso] = useState(null);
  const [fallo, setFallo] = useState('');
  const [nuevaCaja, setNuevaCaja] = useState('');

  const traer = useCallback(async () => {
    setCargando(true);
    try {
      const { data } = await api.get('/api/red/sin-ubicar?solo=fibra');
      setPendientes(data.clientes || []);
    } catch (e) { setFallo(e.response?.data?.error || 'No se pudo leer la lista'); }
    finally { setCargando(false); }
  }, []);

  useEffect(() => { traer(); }, [traer]);

  const lista = pendientes.filter(c =>
    !busca || String(c.nombre || '').toLowerCase().includes(busca.toLowerCase()));

  const alternar = (id) =>
    setMarcados(xs => (xs.includes(id) ? xs.filter(x => x !== id) : [...xs, id]));

  /** Marcar los que se ven, no los 108: quien filtró por «canalon» quiere esos. */
  const marcarVisibles = () => setMarcados(lista.map(c => c.id));

  const cajaElegida = sitios.find(x => x.id === caja);

  const crearCaja = async () => {
    if (!nuevaCaja.trim()) return;
    setOcupado(true); setFallo('');
    try {
      const { data } = await api.post('/api/red/sitios', { nombre: nuevaCaja.trim(), tipo: 'caja' });
      setNuevaCaja('');
      if (alConectar) await alConectar();
      if (data?.id) setCaja(data.id);
      setAviso({ texto: `Caja «${nuevaCaja.trim()}» creada. Ya puedes colgarle clientes.` });
    } catch (e) { setFallo(e.response?.data?.error || 'No se pudo crear la caja'); }
    finally { setOcupado(false); }
  };

  const conectar = async () => {
    setOcupado(true); setFallo(''); setAviso(null);
    try {
      const { data } = await api.put('/api/red/clientes/conectar-lote', {
        cliente_ids: marcados,
        sitio_id: caja,
        puerto_desde: puerto === '' ? null : Number(puerto),
      });
      setAviso({
        texto: `${marcados.length} clientes conectados a ${cajaElegida?.nombre || 'la caja'}.`,
        extra: data?.aviso || null,
      });
      setMarcados([]);
      await traer();
      if (alConectar) alConectar();
    } catch (e) { setFallo(e.response?.data?.error || 'No se pudieron conectar'); }
    finally { setOcupado(false); }
  };

  return (
    <>
      <div style={s.panel}>
        <div style={s.tituloP}>1 · ¿A qué caja los vas a colgar?</div>
        <div style={{ display: 'flex', gap: '9px', flexWrap: 'wrap', alignItems: 'center' }}>
          <select value={caja} onChange={(e) => setCaja(e.target.value)}
            style={{ ...s.input, flex: '1 1 280px', minWidth: 0 }}>
            <option value="">Escoge una caja…</option>
            {sitios.map(x => (
              <option key={x.id} value={x.id}>
                {x.nombre}{x.pon ? ` · PON ${x.pon}` : ''}{x.clientes ? ` · ${x.clientes} ya colgados` : ''}
              </option>
            ))}
          </select>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <span style={{ fontSize: '12.5px', color: 'rgba(255,255,255,0.55)' }}>Primer puerto</span>
            <input value={puerto} onChange={(e) => setPuerto(e.target.value.replace(/[^0-9]/g, ''))}
              placeholder="opcional" style={{ ...s.input, width: '96px' }} />
          </div>
        </div>
        <div style={{ ...s.pie, marginTop: '7px' }}>
          Si pones el primer puerto, los demás se numeran solos en el orden de la lista.
        </div>

        <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap',
                      paddingTop: '12px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <input value={nuevaCaja} onChange={(e) => setNuevaCaja(e.target.value)}
            placeholder="¿No está? Escribe el nombre de la caja nueva"
            style={{ ...s.input, flex: '1 1 260px', minWidth: 0 }} />
          <button type="button" onClick={crearCaja} disabled={ocupado || !nuevaCaja.trim()}
            style={{ ...s.pest, ...(nuevaCaja.trim() ? s.pestOn : {}) }}>+ Crear caja</button>
        </div>
      </div>

      <div style={s.panel}>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center',
                      marginBottom: '10px' }}>
          <div style={{ ...s.tituloP, marginBottom: 0, flex: '1 1 auto' }}>
            2 · ¿Cuáles clientes? <span style={{ color: 'rgba(255,255,255,0.45)', fontWeight: 400 }}>
              — {pendientes.length} sin colgar de ninguna caja</span>
          </div>
          <input value={busca} onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar por nombre…" style={{ ...s.input, flex: '0 1 220px' }} />
          <button type="button" onClick={marcarVisibles} disabled={!lista.length}
            style={s.pest}>Marcar los {lista.length} que veo</button>
          {marcados.length > 0 && (
            <button type="button" onClick={() => setMarcados([])} style={s.pest}>Quitar marcas</button>
          )}
        </div>

        {cargando && <div style={s.sub}>Leyendo…</div>}
        {!cargando && !pendientes.length && (
          <div style={{ ...s.aviso, color: '#BBF7D0', background: 'rgba(34,197,94,0.1)',
                        borderColor: 'rgba(34,197,94,0.4)', marginTop: 0 }}>
            <b>Todos tus clientes de fibra ya están colgados de una caja.</b> No queda nada por
            conectar.
          </div>
        )}

        {lista.length > 0 && (
          <div style={{ maxHeight: '340px', overflowY: 'auto',
                        border: '1px solid rgba(255,255,255,0.08)', borderRadius: '9px' }}>
            {lista.map(c => (
              <label key={c.id} style={{ display: 'flex', gap: '10px', alignItems: 'center',
                                         padding: '9px 12px', cursor: 'pointer',
                                         borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                <input type="checkbox" checked={marcados.includes(c.id)}
                  onChange={() => alternar(c.id)}
                  style={{ width: 'auto', accentColor: COLOR.acento }} />
                <span style={{ fontSize: '13px' }}>{c.nombre}</span>
                {!c.latitud && <span style={{ ...s.pill, marginLeft: 'auto',
                                              background: 'rgba(252,211,77,0.12)', color: COLOR.aviso,
                                              border: '1px solid rgba(252,211,77,0.35)' }}>sin ubicar</span>}
              </label>
            ))}
          </div>
        )}
      </div>

      {fallo && <div style={{ ...s.aviso, color: '#FCA5A5', background: 'rgba(239,68,68,0.1)',
                              borderColor: 'rgba(239,68,68,0.35)' }}>{fallo}</div>}

      {aviso && (
        <div style={{ ...s.aviso, color: '#BBF7D0', background: 'rgba(34,197,94,0.1)',
                      borderColor: 'rgba(34,197,94,0.4)' }}>
          <b>{aviso.texto}</b>
          {aviso.extra && <div style={{ marginTop: '5px', color: '#FDE68A' }}>{aviso.extra}</div>}
        </div>
      )}

      <div style={{ position: 'sticky', bottom: '12px', marginTop: '14px' }}>
        <button type="button" onClick={conectar}
          disabled={ocupado || !caja || !marcados.length}
          style={{ ...s.pest, width: '100%', padding: '13px',
                   ...(caja && marcados.length
                     ? { background: 'rgba(34,197,94,0.18)', color: COLOR.ok,
                         borderColor: 'rgba(34,197,94,0.55)' }
                     : {}) }}>
          {ocupado ? 'Conectando…'
            : !caja ? 'Escoge una caja arriba'
            : !marcados.length ? 'Marca al menos un cliente'
            : `🔌 Conectar ${marcados.length} ${marcados.length === 1 ? 'cliente' : 'clientes'} a ${cajaElegida?.nombre || ''}`
              + (puerto !== '' ? ` — desde el puerto ${puerto}` : '')}
        </button>
      </div>
    </>
  );
}

